import { useState } from "react";
import { useGetOperationsAlerts } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTierBadge } from "./alert-tier-badge";
import type { AlertTier } from "@/lib/alert-tier";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/auth-context";

const TIERS: (AlertTier | "All")[] = ["All", "Black", "Red", "Green", "Healthy"];

export function AlertsTab() {
  const { data: alerts = [], isLoading } = useGetOperationsAlerts({});
  const [tier, setTier] = useState<AlertTier | "All">("All");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const { toast } = useToast();
  const filtered = tier === "All" ? alerts : alerts.filter((a) => a.tier === tier);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Alerts ({alerts.length})</CardTitle>
        <div className="flex gap-2 mt-2">
          {TIERS.map((t) => (
            <Button key={t} size="sm" variant={tier === t ? "default" : "outline"} onClick={() => setTier(t)}>
              {t}{t !== "All" ? ` (${alerts.filter((a) => a.tier === t).length})` : ""}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-8">{tier === "All" ? "No alerts. Stock looks healthy." : `No ${tier} alerts.`}</div>
        ) : (
          <div className="flex flex-col gap-2">
            {filtered.map((a) => (
              <div key={a.itemId} className="flex items-center justify-between rounded border p-3">
                <div className="flex items-center gap-3">
                  <AlertTierBadge tier={a.tier} />
                  <div>
                    <div className="text-sm font-medium">{a.itemName}</div>
                    <div className="text-xs text-muted-foreground">
                      {a.currentStock} {a.unitOfMeasurement ?? ""} in stock · reorder at {a.reorderLevel ?? "—"}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => toast({ title: "Raise PO — opens Stock In form (TODO wire)" })}>Raise PO</Button>
                  <Button size="sm" variant="ghost" onClick={() => toast({ title: "Snoozed for 24 hours." })}>Snooze 24h</Button>
                  {isAdmin ? <Button size="sm" variant="ghost" onClick={() => toast({ title: "Marked resolved." })}>Mark resolved</Button> : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
